import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import PatternHintsBanner from '@/components/PatternHintsBanner';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AlertTriangle, Clock, MessageSquare, ChevronRight, Loader2, Check,
  Shield, Activity, Send, ArrowUpRight, Eye
} from 'lucide-react';

const TRIAGE_COLORS = { urgent: '#C85C5C', clinical_priority: '#C87B7B', same_day: '#D4A843', routine: '#4E7A6B' };
const STATUS_LABELS = { open: 'Open', assigned: 'Assigned', in_progress: 'In Progress', waiting_user: 'Awaiting User', escalated: 'Escalated', resolved: 'Resolved', closed: 'Closed' };

export default function SupportDashboard() {
  const { user: currentUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('real'); // 'real' | 'demo' | 'all'
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showOutcome, setShowOutcome] = useState(false);
  const [outcomeTags, setOutcomeTags] = useState([]);
  const [primaryTag, setPrimaryTag] = useState('');
  const [secondaryTags, setSecondaryTags] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [urgentAlerts, setUrgentAlerts] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const demoParam = viewMode === 'demo' ? '&demo=true' : viewMode === 'real' ? '&demo=false' : '';
      const filterParam = filter !== 'all' ? `${filter.includes('_') ? 'triage' : 'status'}=${filter}` : '';
      const ticketUrl = `${API}/support-v2/staff/tickets?${filterParam}${demoParam}`;
      const [dashRes, tickRes, tmplRes, tagsRes, notifRes] = await Promise.all([
        axios.get(`${API}/support-v2/staff/dashboard-v2`).catch(() => axios.get(`${API}/support-v2/staff/dashboard`)),
        axios.get(ticketUrl),
        axios.get(`${API}/support-v2/templates`).catch(() => ({ data: { templates: [] } })),
        axios.get(`${API}/support-v2/outcome-tags`).catch(() => ({ data: { tags: [] } })),
        axios.get(`${API}/support-v2/notifications`).catch(() => ({ data: {} })),
      ]);
      setDashboard(dashRes.data);
      setTickets(tickRes.data?.tickets || []);
      setTemplates(tmplRes.data?.templates || []);
      setOutcomeTags(tagsRes.data?.tags || []);
      setNotifCount(notifRes.data?.unread_count || 0);
      setUrgentAlerts(notifRes.data?.urgent_alerts || []);
    } catch {} finally { setLoading(false); }
  }, [filter, viewMode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Accept ?filter=escalated|urgent|clinical_priority|open from cockpit KPI link
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get('filter');
    const allowed = ['urgent', 'clinical_priority', 'escalated', 'open', 'in_progress', 'all'];
    if (f && allowed.includes(f)) setFilter(f);
  }, [searchParams]);

  const openTicket = async (ticket) => {
    try {
      const res = await axios.get(`${API}/support-v2/tickets/${ticket.id}`);
      setActiveTicket(res.data.ticket);
      setMessages(res.data.messages || []);
    } catch { toast.error('Failed to load ticket'); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeTicket) return;
    try {
      await axios.post(`${API}/support-v2/tickets/${activeTicket.id}/message`, { message: replyText, is_internal: isInternal });
      toast.success(isInternal ? 'Internal note added' : 'Reply sent');
      setReplyText(''); setIsInternal(false);
      const res = await axios.get(`${API}/support-v2/tickets/${activeTicket.id}`);
      setMessages(res.data.messages || []);
      setActiveTicket(res.data.ticket);
    } catch { toast.error('Failed to send'); }
  };

  const handleClaim = async () => {
    if (!activeTicket) return;
    await axios.post(`${API}/support-v2/staff/tickets/${activeTicket.id}/claim`);
    toast.success('Claimed');
    const res = await axios.get(`${API}/support-v2/tickets/${activeTicket.id}`);
    setActiveTicket(res.data.ticket); fetchData();
  };

  const handleEscalate = async (target) => {
    if (!activeTicket) return;
    await axios.post(`${API}/support-v2/staff/tickets/${activeTicket.id}/escalate`, { escalate_to: target, reason: 'Requires higher review' });
    toast.success(`Escalated to ${target}`);
    const res = await axios.get(`${API}/support-v2/tickets/${activeTicket.id}`);
    setActiveTicket(res.data.ticket); setMessages(res.data.messages || []); fetchData();
  };

  const handleResolve = async () => {
    if (!activeTicket) return;
    setShowOutcome(true);
  };

  const handleResolveWithOutcome = async () => {
    if (!primaryTag) { toast.error('Primary outcome tag required'); return; }
    try {
      await axios.post(`${API}/support-v2/staff/tickets/${activeTicket.id}/resolve-v2`, {
        primary_outcome_tag: primaryTag,
        secondary_outcome_tags: secondaryTags,
        resolution_note: replyText || 'Resolved',
      });
      toast.success('Resolved with outcome tags');
      setShowOutcome(false); setPrimaryTag(''); setSecondaryTags([]);
      setActiveTicket(null); setReplyText(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const insertTemplate = async (template) => {
    const sub = await db?.support_tickets?.find_one ? null : null; // Not needed client-side
    const name = activeTicket?.auto_context_snapshot?.condition ? 'there' : 'there';
    try {
      const res = await axios.post(`${API}/support-v2/templates/resolve`, {
        message: template.message,
        context: {
          name: tickets.find(t => t.id === activeTicket?.id)?.subscriber_name || 'there',
          triage_level: activeTicket?.triage_level || '',
          medication: (activeTicket?.auto_context_snapshot?.current_medications || [])[0]?.name || 'your medication',
          symptom: activeTicket?.category?.replace('_', ' ') || 'your symptom',
        },
      });
      setReplyText(res.data?.resolved_message || template.message);
    } catch {
      setReplyText(template.message);
    }
    setShowTemplates(false);
  };

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'urgent', label: 'Urgent' },
    { key: 'clinical_priority', label: 'Clinical' },
    { key: 'open', label: 'Open' },
    { key: 'escalated', label: 'Escalated' },
    { key: 'in_progress', label: 'In Progress' },
  ];

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6" data-testid="support-dashboard">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Care Support OS</h1>
            <p className="text-xs text-adm-tertiary mt-1">Clinical support triage and response</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white/[0.03] rounded-lg p-0.5 border border-white/[0.06]" data-testid="view-mode-toggle">
              {[
                { key: 'real', label: 'Real Users' },
                { key: 'demo', label: 'Demo Users' },
                { key: 'all', label: 'All' },
              ].map(m => (
                <button
                  key={m.key}
                  onClick={() => { setViewMode(m.key); setActiveTicket(null); }}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-['Work_Sans'] transition-colors ${viewMode === m.key ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}
                  data-testid={`view-mode-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {urgentAlerts.length > 0 && (
              <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 text-xs animate-pulse" data-testid="urgent-banner">
                <AlertTriangle className="w-3 h-3 mr-1" /> {urgentAlerts.length} urgent
              </Badge>
            )}
            <button className="relative p-2 rounded-full hover:bg-white/[0.05]" onClick={() => axios.post(`${API}/support-v2/notifications/mark-all-read`).then(fetchData)} data-testid="notif-bell">
              <MessageSquare className="w-5 h-5 text-adm-tertiary" />
              {notifCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[8px] rounded-full flex items-center justify-center">{notifCount}</span>}
            </button>
          </div>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div> : (
          <div className="flex gap-5">
            {/* LEFT: Ticket list */}
            <div className="w-96 flex-shrink-0 space-y-3">
              {/* Stats */}
              {dashboard && (
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[
                    { label: 'Urgent', value: dashboard.urgent, color: '#C85C5C' },
                    { label: 'Clinical', value: dashboard.clinical_priority, color: '#C87B7B' },
                    { label: 'Open', value: dashboard.open, color: '#D4A843' },
                    { label: 'Active', value: dashboard.total_active, color: '#5C6B8A' },
                  ].map(s => (
                    <div key={s.label} className="text-center p-2 rounded-lg" style={{ backgroundColor: s.color + '10' }}>
                      <p className="text-lg font-['Outfit'] font-light" style={{ color: s.color }}>{s.value}</p>
                      <p className="text-[8px] uppercase text-adm-tertiary">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Filters */}
              <div className="flex gap-1 flex-wrap">
                {FILTERS.map(f => (
                  <button key={f.key} onClick={() => { setFilter(f.key); setActiveTicket(null); }}
                    className={`px-2.5 py-1 rounded-full text-[10px] transition-colors ${filter === f.key ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Ticket list */}
              <div className="space-y-1 max-h-[65vh] overflow-y-auto">
                {tickets.map(t => (
                  <button key={t.id} onClick={() => openTicket(t)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${activeTicket?.id === t.id ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'}`}
                    data-testid={`staff-ticket-${t.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: TRIAGE_COLORS[t.triage_level] || '#8A8681' }} />
                      <span className="text-sm text-adm-primary truncate flex-1">{t.subscriber_name}</span>
                      {(t.is_demo || t.subscriber_is_demo) && <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[7px] px-1">DEMO</Badge>}
                      <Badge className="text-[7px] border-0 px-1.5" style={{ backgroundColor: (TRIAGE_COLORS[t.triage_level] || '#8A8681') + '15', color: TRIAGE_COLORS[t.triage_level] }}>
                        {t.triage_level?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-adm-secondary capitalize truncate">{t.category?.replace('_', ' ')}</p>
                    <p className="text-[9px] text-adm-tertiary truncate">{t.last_message_preview}</p>
                    {t.red_flag_detected && <Badge className="bg-rose-500/15 text-rose-400 border-0 text-[7px] mt-1">RED FLAG</Badge>}
                  </button>
                ))}
                {tickets.length === 0 && <p className="text-xs text-adm-tertiary text-center py-8">No tickets matching filter</p>}
              </div>
            </div>

            {/* RIGHT: Detail */}
            <div className="flex-1 min-w-0">
              {!activeTicket ? (
                <div className="flex items-center justify-center h-full text-adm-tertiary">
                  <p className="text-sm">Select a ticket to view details</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="ticket-detail">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-base text-adm-primary capitalize">{activeTicket.category?.replace('_', ' ')}</h2>
                      <p className="text-[10px] text-adm-tertiary">{new Date(activeTicket.created_at).toLocaleString()} &middot; {STATUS_LABELS[activeTicket.status] || activeTicket.status}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={handleClaim} className="border-white/10 text-adm-secondary text-xs h-7">
                        <Shield className="w-3 h-3 mr-1" /> Claim
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEscalate('admin')} className="border-amber-500/20 text-amber-400 text-xs h-7">
                        <ArrowUpRight className="w-3 h-3 mr-1" /> Escalate
                      </Button>
                      <Button size="sm" onClick={handleResolve} className="bg-emerald-500/20 text-emerald-400 border-0 text-xs h-7">
                        <Check className="w-3 h-3 mr-1" /> Resolve
                      </Button>
                    </div>
                  </div>

                  {/* Context snapshot */}
                  {activeTicket.auto_context_snapshot && (
                    <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3">
                      <p className="text-[9px] uppercase tracking-wider text-adm-tertiary mb-2">Clinical Context</p>
                      <div className="grid grid-cols-3 gap-2 text-[10px]">
                        {activeTicket.auto_context_snapshot.condition && <div><span className="text-adm-tertiary">Condition: </span><span className="text-adm-secondary">{activeTicket.auto_context_snapshot.condition}</span></div>}
                        {activeTicket.auto_context_snapshot.phase != null && <div><span className="text-adm-tertiary">Phase: </span><span className="text-adm-secondary">{activeTicket.auto_context_snapshot.phase}</span></div>}
                        {activeTicket.auto_context_snapshot.today_state && <div><span className="text-adm-tertiary">State: </span><span className="text-adm-secondary">{activeTicket.auto_context_snapshot.today_state}</span></div>}
                        {activeTicket.auto_context_snapshot.current_medications?.length > 0 && (
                          <div className="col-span-3"><span className="text-adm-tertiary">Meds: </span><span className="text-adm-secondary">{activeTicket.auto_context_snapshot.current_medications.map(m => m.name).join(', ')}</span></div>
                        )}
                        {activeTicket.auto_context_snapshot.latest_labs && (
                          <div className="col-span-3"><span className="text-adm-tertiary">Labs: </span><span className="text-adm-secondary">{activeTicket.auto_context_snapshot.latest_labs.abnormal_markers?.join(', ') || 'Normal'}</span></div>
                        )}
                      </div>
                    </CardContent></Card>
                  )}

                  {/* Pattern hints (shared deterministic banner) */}
                  {activeTicket.subscriber_id && (
                    <Card className="bg-adm-panel border-white/[0.06]" data-testid="support-pattern-hints"><CardContent className="p-3">
                      <p className="text-[9px] uppercase tracking-wider text-adm-tertiary mb-2">Tracking Pattern Hints</p>
                      <PatternHintsBanner
                        subscriberId={activeTicket.subscriber_id}
                        viewerRole={currentUser?.role}
                      />
                    </CardContent></Card>
                  )}

                  {/* Structured payload */}
                  {activeTicket.structured_payload && Object.keys(activeTicket.structured_payload).length > 0 && (
                    <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3">
                      <p className="text-[9px] uppercase tracking-wider text-adm-tertiary mb-2">Structured Intake</p>
                      <div className="space-y-1">
                        {Object.entries(activeTicket.structured_payload).filter(([_, v]) => v).map(([k, v]) => (
                          <div key={k} className="flex gap-2 text-[10px]">
                            <span className="text-adm-tertiary capitalize w-28 flex-shrink-0">{k.replace('_', ' ')}:</span>
                            <span className="text-adm-secondary">{v}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent></Card>
                  )}

                  {/* Messages */}
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {messages.map(msg => (
                      <div key={msg.id} className={`p-3 rounded-lg ${
                        msg.message_type === 'auto_summary' ? 'bg-white/[0.02] border border-white/[0.06]' :
                        msg.is_internal ? 'bg-amber-500/5 border border-amber-500/10' :
                        msg.sender_type === 'subscriber' ? 'bg-white/[0.04]' : 'bg-emerald-500/5 border border-emerald-500/10'
                      }`} data-testid={`staff-msg-${msg.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] text-adm-tertiary font-medium">{msg.sender_name || msg.sender_type}</span>
                          {msg.is_internal && <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[7px]">Internal</Badge>}
                          <span className="text-[8px] text-adm-tertiary ml-auto">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-xs text-adm-secondary whitespace-pre-line">{msg.message_text}</p>
                      </div>
                    ))}
                  </div>

                  {/* Reply */}
                  {activeTicket.status !== 'resolved' && activeTicket.status !== 'closed' && (
                    <div className="space-y-2">
                      {/* Template selector */}
                      <div className="flex items-center gap-2">
                        <button onClick={() => setShowTemplates(!showTemplates)} className="text-[10px] text-adm-accent hover:text-adm-accent/80" data-testid="insert-template">
                          Insert Template
                        </button>
                        {showTemplates && (
                          <div className="absolute z-10 bg-adm-panel border border-white/10 rounded-lg shadow-lg p-2 max-h-40 overflow-y-auto w-64">
                            {templates.map(t => (
                              <button key={t.id} onClick={() => insertTemplate(t)} className="w-full text-left px-2 py-1.5 text-xs text-adm-secondary hover:bg-white/[0.04] rounded">
                                {t.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                          placeholder={isInternal ? "Internal clinical note (not visible to subscriber)..." : "Reply to subscriber..."}
                          className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[50px] flex-1" data-testid="staff-reply" />
                        <div className="flex flex-col gap-1">
                          <Button size="sm" onClick={sendReply} disabled={!replyText.trim()} className="bg-adm-accent/20 text-adm-accent border-0 text-xs h-8" data-testid="staff-send">
                            <Send className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-[10px] text-adm-tertiary cursor-pointer">
                        <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded border-white/20" />
                        Internal note (staff only)
                      </label>
                    </div>
                  )}

                  {/* Outcome tagging modal */}
                  {showOutcome && (
                    <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-3" data-testid="outcome-modal">
                      <p className="text-sm text-adm-primary font-medium">Resolution — Outcome Tagging</p>
                      <div>
                        <p className="text-[10px] text-adm-tertiary mb-1">Primary outcome *</p>
                        <div className="flex flex-wrap gap-1.5">
                          {outcomeTags.map(tag => (
                            <button key={tag} onClick={() => setPrimaryTag(tag)}
                              className={`px-2.5 py-1 rounded-full text-[10px] transition-colors ${primaryTag === tag ? 'bg-adm-accent/20 text-adm-accent' : 'bg-white/[0.04] text-adm-tertiary hover:text-adm-secondary'}`}>
                              {tag.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] text-adm-tertiary mb-1">Secondary tags (optional)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {outcomeTags.filter(t => t !== primaryTag).map(tag => (
                            <button key={tag} onClick={() => setSecondaryTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                              className={`px-2.5 py-1 rounded-full text-[10px] transition-colors ${secondaryTags.includes(tag) ? 'bg-white/10 text-adm-secondary' : 'bg-white/[0.02] text-adm-tertiary hover:text-adm-secondary'}`}>
                              {tag.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Resolution note..."
                        className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[40px]" />
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setShowOutcome(false)} className="border-white/10 text-adm-tertiary text-xs">Cancel</Button>
                        <Button size="sm" onClick={handleResolveWithOutcome} className="bg-emerald-500/20 text-emerald-400 border-0 text-xs" data-testid="confirm-resolve">
                          <Check className="w-3 h-3 mr-1" /> Resolve
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
